import type { ComponentType } from 'react';
import type { Feature } from '../../lib/features';
import type { EventView } from '../../lib/events';
import type { PanelProps } from './types';
import type { EventTabKey } from '../../contracts';
import DashboardPanel from './DashboardPanel';
import FollowupsPanel from './FollowupsPanel';
import DuringDay from './DuringDay';
import { ReimbursementsPanel, HonorariaPanel } from './LedgerPanel';
import { NextYearNotes } from './NotesPanel';
import FeedbackPanel from './FeedbackPanel';
import { registeredPanel, Stage } from './registry';

// The event page, stage by stage. A track plugs its panel in with one import and a
// `component` on its tab (or registerPanel in registry.ts from elsewhere); a tab without
// one shows "Coming with …".
export type { Stage, Slot } from './registry';
export { registerPanel, registerSlot, slotFor } from './registry';

export interface StageTab<S extends Stage = Stage> {
  key: EventTabKey<S>;
  label: string;
  feature?: Feature;
  comingWith?: string;
  component?: ComponentType<PanelProps>;
}

export const STAGE_LABEL: Record<Stage, string> = { before: 'Before', during: 'During', after: 'After' };

export const STAGES: { [S in Stage]: StageTab<S>[] } = {
  before: [
    { key: 'overview', label: 'Overview', component: DashboardPanel },
    { key: 'followups', label: 'Follow-ups', component: FollowupsPanel },
    { key: 'people', label: 'People' },
    { key: 'registration', label: 'Registration & outreach' },
    { key: 'speakers', label: 'Speakers & agenda', comingWith: 'speakers and sessions' },
    { key: 'hotel', label: 'Hotel', comingWith: 'hotel nights and the rooming list' },
    { key: 'catering', label: 'Catering', comingWith: 'catering numbers per meal' },
    { key: 'logistics', label: 'Day-of' },
    { key: 'budget', label: 'Budget & vendors' },
    { key: 'emails', label: 'Emails' },
    { key: 'timeline', label: 'Timeline' },
    { key: 'documents', label: 'Documents' },
  ],
  during: [{ key: 'day', label: 'Day', component: DuringDay }],
  after: [
    { key: 'attendance', label: 'Attendance' },
    { key: 'feedback', label: 'Feedback', component: FeedbackPanel },
    { key: 'recordings', label: 'Recordings' },
    { key: 'report', label: 'Stats & report' },
    { key: 'reimbursements', label: 'Reimbursements', component: ReimbursementsPanel },
    { key: 'honoraria', label: 'Honoraria & gift cards', component: HonorariaPanel },
    { key: 'budget', label: 'Money' },
    { key: 'next_year', label: 'Notes for next year', component: NextYearNotes },
  ],
};

export const panelFor = (stage: Stage, tab: StageTab) => tab.component ?? registeredPanel(stage, tab.key);

const addDays = (day: string, n: number) => {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const daysBetween = (a: string, b: string) =>
  Math.round((new Date(`${b}T12:00:00Z`).getTime() - new Date(`${a}T12:00:00Z`).getTime()) / 86400000);

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`;

/** A span between two dates as its largest unit, rounded down: days up to 30, then months, then years (past 365 days). */
export function spanLabel(from: string, to: string): string {
  const days = daysBetween(from, to);
  if (days <= 30) return plural(days, 'day');
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  const months = Math.max(1, (ty - fy) * 12 + (tm - fm) - (td < fd ? 1 : 0));
  return days <= 365 ? plural(months, 'month') : plural(Math.max(1, Math.floor(months / 12)), 'year');
}

/** Which stage today is in (Toronto dates), the event day to open, and the countdown line. */
export function stageOf(
  event: Pick<EventView, 'days'>,
  today: string,
): { stage: Stage; day: string; countdown: string } {
  const dates = event.days.map((d) => d.date);
  const first = dates[0];
  const last = dates[dates.length - 1];
  if (today < first) {
    const n = daysBetween(today, first);
    return { stage: 'before', day: first, countdown: n === 1 ? 'Tomorrow' : `${spanLabel(today, first)} to go` };
  }
  if (today > last) {
    const n = daysBetween(last, today);
    return {
      stage: 'after',
      day: last,
      countdown: n === 1 ? 'Ended yesterday' : `Ended ${spanLabel(last, today)} ago`,
    };
  }
  const index = dates.indexOf(today);
  if (index >= 0) return { stage: 'during', day: today, countdown: `Day ${index + 1} today` };
  const next = dates.find((d) => d > today) ?? last;
  return { stage: 'during', day: next, countdown: `Day ${dates.indexOf(next) + 1} on ${next}` };
}

export { addDays };
